import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import {
  ActionType,
  ALL,
  DurationEnum,
  RoleType,
  UserMapDataType,
} from '../../types/analyticTypes';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { getUsersForRole } from './getUsersForRole';

type ActionDurationFilterProps = {
  option: DurationEnum;
  actions: ActionType[];
  role: RoleType;
  users: string[];
  userMap: UserMapDataType;
  onFilterChange: (
    selectedOption: DurationEnum,
    selectedActions: ActionType[],
    selectedRole: RoleType,
    selectedUsers: string[]
  ) => void;
};

const ActionDurationFilter: React.FC<ActionDurationFilterProps> = (props) => {
  const { option, actions, role, users, userMap, onFilterChange } = props;
  const [selectedOption, setSelectedOption] = useState<DurationEnum>(option);
  const [selectedActions, setSelectedActions] = useState<ActionType[]>(actions);
  const [selectedRole, setSelectedRole] = useState<RoleType>(role);
  const [selectedUsers, setSelectedUsers] = useState<string[]>(users);

  const userOptions = getUsersForRole(userMap, selectedRole);
  const noUsersForRole = selectedRole !== RoleType.all && userOptions.length <= 1;

  const handleDurationChange = (event: SelectChangeEvent) => {
    setSelectedOption(event.target.value as DurationEnum);
  };

  const handleActionTypeChange = (event: SelectChangeEvent<ActionType[]>) => {
    const updatedValues = event.target.value as ActionType[];

    const finalValues =
      updatedValues.includes(ActionType.all) &&
      updatedValues[updatedValues.length - 1] === ActionType.all
        ? [ActionType.all]
        : updatedValues.filter((type) => type !== ActionType.all);

    setSelectedActions(finalValues);
  };

  const handleRoleTypeChange = (event: SelectChangeEvent) => {
    const newRole = event.target.value as RoleType;
    setSelectedRole(newRole);

    const allowedUsers = getUsersForRole(userMap, newRole);

    const validSpecificUsers = selectedUsers.filter(
      (email) => email !== ALL && allowedUsers.includes(email)
    );

    setSelectedUsers(validSpecificUsers.length > 0 ? validSpecificUsers : [ALL]);
  };

  const handleUserChange = (event: React.SyntheticEvent, value: string[]) => {
    if (value.length === 0) {
      setSelectedUsers([ALL]);
      return;
    }

    const finalValues =
      value.includes(ALL) && value[value.length - 1] === ALL
        ? [ALL]
        : value.filter((email) => email !== ALL);

    setSelectedUsers(finalValues);
  };

  const getDropdownLabel = (selected: ActionType[]): string => {
    if (selected.length === 1) {
      return selected[0] === ActionType.all ? 'All actions' : selected[0];
    }

    return `${selected.length} actions selected`;
  };

  const onApply = () => {
    onFilterChange(selectedOption, selectedActions, selectedRole, selectedUsers);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <FormControl size="medium" style={{ minWidth: '100px' }}>
        <InputLabel>Duration</InputLabel>
        <Select
          value={selectedOption}
          label="duration"
          sx={{ textTransform: 'capitalize' }}
          onChange={handleDurationChange}
        >
          {Object.values(DurationEnum).map((option) => (
            <MenuItem key={option} value={option} sx={{ textTransform: 'capitalize' }}>
              {option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="medium" style={{ minWidth: '300px' }}>
        <InputLabel>Action types</InputLabel>
        <Select
          multiple
          value={selectedActions}
          label="action types"
          onChange={handleActionTypeChange}
          renderValue={getDropdownLabel}
        >
          {Object.values(ActionType).map((option) => (
            <MenuItem key={option} value={option}>
              {option === ActionType.all ? 'All actions' : option}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="medium" style={{ minWidth: '200px' }}>
        <InputLabel>Role</InputLabel>
        <Select value={selectedRole} label="Role" onChange={handleRoleTypeChange}>
          {Object.keys(RoleType).map((role) => (
            <MenuItem key={role} value={role}>
              {role === RoleType.all ? 'All roles' : role}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Autocomplete
        size="medium"
        sx={{ minWidth: '320px' }}
        multiple
        options={userOptions}
        value={selectedUsers}
        onChange={handleUserChange}
        disabled={noUsersForRole}
        disableClearable
        getOptionLabel={(option) => (option === ALL ? 'All users' : option)}
        isOptionEqualToValue={(option, value) => option === value}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={option}>
            {option === ALL ? (
              <Typography variant="body2" color="text.secondary">
                All users
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <span>{option}</span>
                {userMap[option]?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ heigh: 18, fontSize: 10 }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Users"
            error={noUsersForRole}
            helperText={
              noUsersForRole ? 'Not found users with this role - please change the role' : undefined
            }
          />
        )}
      />
      <Button variant="contained" onClick={onApply}>
        Apply
      </Button>
    </Box>
  );
};

export default ActionDurationFilter;
